/**
 * ComposeText — Tiptap-backed rich text editor used by the invitation step on
 * /courses/:id/configure and /programs/:id/configure. Visual reproduction of
 * the "Compose the Text" panel from Figma node 11126:513918.
 *
 * Media-attachment controls (image button + paperclip/gallery icons) are
 * intentionally omitted — invitations are text-only.
 *
 * - `onSend` is optional. When provided, a "Send" button renders bottom-right.
 * - The `.compose-editor .ProseMirror` styles live in the global stylesheet.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { EASE_OUT } from '@/lib/motion';

const FONT = 'Poppins, sans-serif';

// ─── Public API ──────────────────────────────────────────────────────────────

export interface ComposeTextProps {
  value: string;
  onChange: (next: string) => void;
  /** Optional Send action. Hidden when not provided. */
  onSend?: () => void;
  /** Toolbar / placeholder copy override. */
  placeholder?: string;
  /**
   * When true, the editor renders as a compact "optional" CTA until the user
   * opens it. Once content exists it stays expanded automatically. Useful in
   * contexts (e.g. the invitation step on /configure pages) where writing
   * text is optional and the full editor would otherwise dominate the layout.
   */
  collapsible?: boolean;
  /** Label shown on the collapsed CTA. */
  collapsedLabel?: string;
  /** Hint shown under the collapsed CTA label. */
  collapsedHint?: string;
}

export default function ComposeText({
  value,
  onChange,
  onSend,
  placeholder = 'Write something awesome...',
  collapsible = false,
  collapsedLabel = 'Compose a text',
  collapsedHint = 'Optional — click to personalize your message',
}: ComposeTextProps) {
  const [fullscreen, setFullscreen] = useState(false);
  const [headingOpen, setHeadingOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => !collapsible || !!value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Tiptap returns "<p></p>" for empty content; treat it as ""
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Keep the editor in sync if the parent swaps `value` (e.g. switching
  // between audiences). Avoid replacing content while focused — that would
  // steal the cursor mid-typing. The `isDestroyed` guard is essential under
  // React StrictMode: useEditor tears down and recreates the editor on the
  // mount double-invoke, and running a command on the stale destroyed instance
  // throws ("schema.cached" is null on a torn-down ProseMirror view).
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (editor.isFocused) return;
    const current = editor.getHTML();
    if ((value || '') !== (current === '<p></p>' ? '' : current)) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [editor, value]);

  if (collapsible && !expanded) {
    return (
      <motion.button
        type="button"
        onClick={() => setExpanded(true)}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, ease: EASE_OUT }}
        className="group w-full flex items-center gap-4 px-5 py-4 rounded-[20px] border border-border-primary/10 hover:border-primary-500/30 text-left active:scale-[0.995] hover:brightness-[1.02] transition-[border-color,transform,filter] duration-150"
        style={{
          fontFamily: FONT,
          background:
            'linear-gradient(135deg, rgb(var(--color-primary) / 0.06) 0%, rgb(var(--color-secondary) / 0.06) 60%, rgb(var(--color-primary) / 0.04) 100%)',
        }}
        aria-expanded={false}
      >
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-full text-text-secondary/70 group-hover:text-primary-500 group-hover:bg-primary-500/[0.08] transition-colors duration-150"
          aria-hidden="true"
        >
          <PencilIcon />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-text-primary">
            {collapsedLabel}
          </span>
          <span className="block text-xs text-text-tertiary mt-0.5">
            {collapsedHint}
          </span>
        </span>
        <span
          className="text-text-tertiary group-hover:text-primary-500 transition-colors duration-150"
          aria-hidden="true"
        >
          <ChevronDown />
        </span>
      </motion.button>
    );
  }

  if (!editor) {
    return (
      <div
        className="relative overflow-hidden rounded-[20px] px-[18px] py-[14px] border border-border-primary/10 min-h-[420px]"
        style={{
          background:
            'linear-gradient(135deg, rgb(var(--color-primary) / 0.06) 0%, rgb(var(--color-secondary) / 0.06) 60%, rgb(var(--color-primary) / 0.04) 100%)',
        }}
        aria-busy="true"
      />
    );
  }

  const headingLabel = editor.isActive('heading', { level: 1 })
    ? 'Heading 1'
    : editor.isActive('heading', { level: 2 })
      ? 'Heading 2'
      : editor.isActive('heading', { level: 3 })
        ? 'Heading 3'
        : 'Body';

  const handleAlign = (align: 'left' | 'center' | 'right' | 'justify') => {
    editor.chain().focus().setTextAlign(align).run();
  };

  const handleLinkPrompt = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleClearFmt = () => {
    editor.chain().focus().unsetAllMarks().clearNodes().run();
  };

  return (
    <motion.div
      initial={collapsible ? { opacity: 0, y: -6, scale: 0.985 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2, ease: EASE_OUT }}
      className="relative overflow-hidden rounded-[20px] px-[18px] py-[14px] border border-border-primary/10"
      style={{
        background:
          'linear-gradient(135deg, rgb(var(--color-primary) / 0.06) 0%, rgb(var(--color-secondary) / 0.06) 60%, rgb(var(--color-primary) / 0.04) 100%)',
        transformOrigin: 'top center',
      }}
    >
      <header className="flex items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-bold text-text-primary leading-7" style={{ fontFamily: FONT }}>
          Compose the Text
        </h3>
        <div className="flex items-center gap-1">
          {collapsible && !value && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label="Hide editor"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-text-secondary/70 hover:bg-text-tertiary/10 hover:text-text-primary active:scale-[0.94] transition-[background-color,color,transform] duration-150"
            >
              <ChevronUp />
            </button>
          )}
          <button
            type="button"
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? 'Exit fullscreen' : 'Expand editor'}
            aria-pressed={fullscreen}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-text-secondary/70 hover:bg-text-tertiary/10 hover:text-text-primary active:scale-[0.94] transition-[background-color,color,transform] duration-150"
          >
            {fullscreen ? <ContractIcon /> : <ExpandIcon />}
          </button>
        </div>
      </header>

      {/* Toolbar */}
      <div
        className="rounded-2xl bg-background-primary/60 dark:bg-grey-800/40 border border-white dark:border-white/10 shadow-[-5px_5px_7px_0_rgba(0,0,0,0.08)] px-2.5 py-2 mb-4 flex items-center gap-1.5 flex-wrap"
        role="toolbar"
        aria-label="Text formatting"
      >
        {/* Heading dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setHeadingOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={headingOpen}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-background-primary/85 dark:bg-grey-700/70 border border-white dark:border-white/10 shadow-[-2px_1px_2px_0_rgba(0,0,0,0.06)] active:scale-[0.97] transition-transform duration-150"
          >
            <span className="text-sm font-semibold text-text-primary" style={{ fontFamily: FONT }}>
              {headingLabel}
            </span>
            <ChevronDown />
          </button>
          <AnimatePresence>
            {headingOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close menu"
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setHeadingOpen(false)}
                />
                <motion.ul
                  role="listbox"
                  initial={{ opacity: 0, scale: 0.97, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97, y: -4 }}
                  transition={{ duration: 0.14, ease: EASE_OUT }}
                  className="absolute left-0 top-full mt-1 z-20 min-w-[140px] rounded-xl bg-background-primary border border-border-primary/20 shadow-lg py-1"
                  style={{ transformOrigin: 'top left' }}
                >
                  {[
                    { label: 'Heading 1', cmd: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
                    { label: 'Heading 2', cmd: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
                    { label: 'Heading 3', cmd: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
                    { label: 'Body', cmd: () => editor.chain().focus().setParagraph().run() },
                  ].map((item) => (
                    <li key={item.label}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={headingLabel === item.label}
                        onClick={() => {
                          item.cmd();
                          setHeadingOpen(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-text-tertiary/10 transition-colors ${
                          headingLabel === item.label
                            ? 'text-primary-500 font-bold'
                            : 'text-text-primary'
                        }`}
                        style={{ fontFamily: FONT }}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </motion.ul>
              </>
            )}
          </AnimatePresence>
        </div>

        <ToolGroup>
          <ToolBtn label="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
            <span className="font-extrabold">B</span>
          </ToolBtn>
          <ToolBtn label="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
            <span className="line-through">S</span>
          </ToolBtn>
          <ToolBtn label="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <span className="italic">I</span>
          </ToolBtn>
        </ToolGroup>

        <ToolGroup>
          <ToolBtn label="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <BulletListIcon />
          </ToolBtn>
          <ToolBtn label="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <NumberedListIcon />
          </ToolBtn>
        </ToolGroup>

        <ToolGroup>
          <ToolBtn label="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => handleAlign('left')}>
            <AlignIcon align="left" />
          </ToolBtn>
          <ToolBtn label="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => handleAlign('center')}>
            <AlignIcon align="center" />
          </ToolBtn>
          <ToolBtn label="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => handleAlign('right')}>
            <AlignIcon align="right" />
          </ToolBtn>
          <ToolBtn label="Justify" active={editor.isActive({ textAlign: 'justify' })} onClick={() => handleAlign('justify')}>
            <AlignIcon align="justify" />
          </ToolBtn>
        </ToolGroup>

        <ToolGroup>
          <ToolBtn label="Insert link" active={editor.isActive('link')} onClick={handleLinkPrompt}>
            <LinkIcon />
          </ToolBtn>
          <ToolBtn label="Remove link" onClick={() => editor.chain().focus().unsetLink().run()}>
            <UnlinkIcon />
          </ToolBtn>
        </ToolGroup>

        <ToolGroup>
          <ToolBtn label="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
            <QuoteIcon />
          </ToolBtn>
          <ToolBtn label="Clear formatting" onClick={handleClearFmt}>
            <ClearFmtIcon />
          </ToolBtn>
        </ToolGroup>

        <div className="ml-auto">
          <ToolBtn label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} onClick={() => setFullscreen((v) => !v)} active={fullscreen}>
            {fullscreen ? <ContractIcon /> : <ExpandIcon />}
          </ToolBtn>
        </div>
      </div>

      {/* Editor body */}
      <div
        className={`rounded-2xl bg-background-primary dark:bg-grey-800 border border-transparent focus-within:border-primary-500/40 transition-[border-color,height] duration-200 ${
          fullscreen ? 'min-h-[520px]' : 'min-h-[260px]'
        }`}
      >
        <EditorContent
          editor={editor}
          className="compose-editor px-[11px] py-[16px] text-sm text-text-primary outline-none"
          style={{ fontFamily: FONT, lineHeight: '22px' }}
        />
      </div>

      {onSend && (
        <div className="flex items-center justify-end mt-4">
          <button
            type="button"
            onClick={onSend}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold text-white hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150"
            style={{
              fontFamily: FONT,
              background: `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, ${'rgb(var(--color-primary))'} 70%)`,
            }}
          >
            Send <SendIcon />
          </button>
        </div>
      )}
    </motion.div>
  );
}

// ─── Toolbar primitives ──────────────────────────────────────────────────────

function ToolGroup({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <div className={`inline-flex items-center gap-0.5 ${muted ? 'opacity-40' : ''}`}>
      {children}
    </div>
  );
}

interface ToolBtnProps {
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
}

function ToolBtn({ label, children, onClick, active }: ToolBtnProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      title={label}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[13px] active:scale-[0.92] transition-[background-color,color,transform] duration-150 ${
        active
          ? 'bg-text-tertiary/15 dark:bg-text-tertiary/25 text-text-primary'
          : 'text-text-primary/70 hover:bg-text-tertiary/10 hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Toolbar icons ───────────────────────────────────────────────────────────

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function ChevronDown() {
  return (
    <svg {...ICON_PROPS} width={14} height={14} className="text-text-tertiary">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ChevronUp() {
  return (
    <svg {...ICON_PROPS} width={16} height={16}>
      <polyline points="6 15 12 9 18 15" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg {...ICON_PROPS} width={18} height={18}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function BulletListIcon() {
  return (
    <svg {...ICON_PROPS}>
      <line x1="9" y1="6" x2="20" y2="6" />
      <line x1="9" y1="12" x2="20" y2="12" />
      <line x1="9" y1="18" x2="20" y2="18" />
      <circle cx="5" cy="6" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="5" cy="18" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

function NumberedListIcon() {
  return (
    <svg {...ICON_PROPS}>
      <line x1="10" y1="6" x2="20" y2="6" />
      <line x1="10" y1="12" x2="20" y2="12" />
      <line x1="10" y1="18" x2="20" y2="18" />
      <path d="M4 6h2v0M4 4l2-1v3M4 18a2 2 0 014 0c0 1-3 2-4 3h4M4 11h3l-3 3h3" />
    </svg>
  );
}

function AlignIcon({ align }: { align: 'left' | 'center' | 'right' | 'justify' }) {
  const lines: Array<[number, number, number, number]> =
    align === 'left'
      ? [[3, 6, 21, 6], [3, 10, 15, 10], [3, 14, 21, 14], [3, 18, 15, 18]]
      : align === 'right'
        ? [[3, 6, 21, 6], [9, 10, 21, 10], [3, 14, 21, 14], [9, 18, 21, 18]]
        : align === 'center'
          ? [[3, 6, 21, 6], [6, 10, 18, 10], [3, 14, 21, 14], [6, 18, 18, 18]]
          : [[3, 6, 21, 6], [3, 10, 21, 10], [3, 14, 21, 14], [3, 18, 21, 18]];
  return (
    <svg {...ICON_PROPS}>
      {lines.map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M10 13a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1" />
      <path d="M14 11a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1" />
    </svg>
  );
}

function UnlinkIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M10 13a5 5 0 007 0l1.5-1.5" />
      <path d="M14 11a5 5 0 00-7 0L5.5 12.5" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </svg>
  );
}

function QuoteIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M7 7H4v6h3a3 3 0 013 3v1H6" />
      <path d="M17 7h-3v6h3a3 3 0 013 3v1h-4" />
    </svg>
  );
}

function ClearFmtIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M5 5h14M9 5l-2 14M14 5l-1 7" />
      <line x1="14" y1="17" x2="20" y2="17" />
      <line x1="17" y1="14" x2="17" y2="20" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg {...ICON_PROPS}>
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function ContractIcon() {
  return (
    <svg {...ICON_PROPS}>
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
